import googlemap from "javascripts/googlemap";
import Rails from "@rails/ujs";
import { embla } from "javascripts/webpage/embla";
import { unmaskSlash } from "javascripts/webpage/link";
import { bindMicromodalEventTrigger } from "javascripts/micromodal";

class SpotMap {
  constructor(maps, url) {
    this.maps = maps;
    this.url = unmaskSlash(url);
    this.images = {};
    this.spots = {};
    this.markers = {};
    this.pressParts = document.querySelectorAll(".p-presses-show__part[data-id]");
    this.spotSlugs = [].filter.call(this.pressParts, (node) => /Component::Travel|Component::Tabelog/.test(node.dataset.type)).map((node) => node.dataset.id);
    this.observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry) => {
            this.currentSpotSlug = Object.keys(this.slugs).find((key) => this.slugs[key].includes(entry.target.dataset.id)) || this.spotSlugs[this.spotSlugs.length - 1];
            if (Object.keys(this.markers).length) {
              const marker = Object.values(this.markers).find((marker) => marker.slug === this.currentSpotSlug);
              if (marker) this.maps.event.trigger(marker, "click");
            }
          });
      },
      { rootMargin: "-50% 0px -50% 0px" },
    );
    let slugs = [];
    this.slugs = [].reduce.call(
      this.pressParts,
      (accumulator, node) => {
        this.observer.observe(node);
        slugs.push(node.dataset.id);
        if (this.spotSlugs.includes(node.dataset.id)) {
          accumulator[node.dataset.id] = [...slugs];
          slugs = [];
        }
        return accumulator;
      },
      {},
    );

    this.map = new maps.Map(document.getElementById("js-spot-map-canvas"), {
      mapTypeId: maps.MapTypeId.ROADMAP,
      fullscreenControl: false,
      mapTypeControl: false,
      gestureHandling: "greedy",
      streetViewControl: false,
      zoomControl: false,
      disableDefaultUI: true,
    });

    this.iconHash = (type, active, selected) => {
      if (selected) {
        return { url: this.images[`${type}_here`], scaledSize: new this.maps.Size(40, 40) };
      } else {
        if (active) {
          return { url: this.images[`${type}_active`], scaledSize: new this.maps.Size(48, 48) };
        } else {
          return { url: this.images[type], scaledSize: new this.maps.Size(32, 32) };
        }
      }
    };

    this.toggleSlide = () => {
      document.body.classList.add("c-slide-panel__in");
      document.body.classList.toggle("c-slide-panel__in--right");
      document.querySelector(".js-slide-panel").classList.toggle("c-slide-panel--open");
    };

    this.infoWindowContent = (spot) => {
      const image_tag = spot.image_url ? `<img class="c-spotmap-infowindow__image" src="${spot.image_url}">` : '<div class="c-spotmap__image--empty"></div>';
      return `
        <div class="c-spotmap-infowindow__container">
          <a href="${spot.url}" target="_blank" class="c-spotmap-infowindow__link">
            <figure class="c-spotmap-infowindow__figure">${image_tag}</figure>
          </a>
          <div class="c-spotmap-infowindow__content">
            <p class="c-spotmap-infowindow__name">${spot.name.slice(0, spot.name.indexOf("（"))}</p>
            <div class="c-spotmap-infowindow__rating">
              <div class="c-rating"><div class="c-rating__star"><i class="c-rating__star--value${Math.floor(spot.score / 0.5)}"></i></div></div>
              <span class="c-rating__score">${spot.score}</span>
            </div>
            <p class="c-spotmap-infowindow__description">${spot.description || ""}</p>
          </div>
        </div>
      `;
    };

    this.makeSpotHtml = (spot, index) => {
      const smartphone = document.documentElement.classList.contains("SmartView");
      const image_tag = spot.image_url ? `<img class="c-spotmap__image" src="${spot.image_url}">` : `<div class="c-spotmap__image--empty"></div>`;
      const score_tag = spot.score
        ? `
          <div class="c-spotmap__rating">
            <div class="c-rating"><div class="c-rating__star"><i class="c-rating__star--value${Math.floor(spot.score / 0.5)}"></i></div></div>
            <span class="c-rating__score">${spot.score}</span>
          </div>
        `
        : "";
      const distance_tag = spot.distance ? `<p class="c-spotmap__distance">${spot.distance}</p>` : "";
      const open_map_app = smartphone
        ? `
          <a target="_blank" class="c-spotmap__link--app js-click-count" data-click-area="map_google_map" data-event="event16"
            href="https://maps.google.com/maps?q=${spot.latitude},${spot.longitude}">地図アプリで開く</a>
        `
        : "";
      const description_tag = spot.description ? `<p class="c-spotmap__description">${spot.description}</p>` : "";
      const memo_tag = spot.memo
        ? `
          <div class="c-spotmap__memo" data-micromodal-trigger="target-lists-spot-memo-${spot.id}">
            <i class="glyph-memo c-glyph c-glyph--left"></i><span class="c-spotmap__memo--lineclamp">${spot.memo}</span>
          </div>
        `
        : "";

      let relationship = "";
      if (spot.url.match(/cid=icotto/)) relationship = "nofollow ";
      return `
        <li class="c-embla__item">
          <div class="c-spotmap__item" data-marker-id="${spot.id}" data-slide-index="${index}">
            <a href="${spot.url}" target="_blank" rel="${relationship}" class="c-spotmap__link--image">${image_tag}</a>
            <a href="${spot.url}" target="_blank" rel="${relationship}" class="c-spotmap__link">
              <p class="c-spotmap__name">${spot.name}</p>
            </a>
            ${score_tag}${description_tag}${distance_tag}${open_map_app}
          </div>
          ${memo_tag}
        </li>
      `;
    };

    this.makeMarker = (spot) => {
      const marker = new this.maps.Marker({
        id: spot.id,
        slug: spot.slug,
        type: spot.type,
        selected: spot.selected,
        map: this.map,
        position: new this.maps.LatLng(spot.latitude, spot.longitude),
        icon: this.iconHash(spot.type, false, spot.selected),
        zIndex: spot.selected ? 2 : 1,
      });
      if (marker.selected) {
        const infoWindow = new this.maps.InfoWindow({ content: this.infoWindowContent(spot) });
        marker.addListener("click", () => infoWindow.open(marker.map, marker));
      } else {
        marker.addListener("click", () => {
          const spotItem = document.querySelector(`.c-spotmap__item[data-marker-id="${spot.id}"]`);
          this.emblaApi && this.emblaApi.scrollTo(parseInt(spotItem.dataset.slideIndex));
        });
      }
      return marker;
    };

    this.makeMarkers = () => {
      this.bounds = new maps.LatLngBounds();
      Object.values(this.markers).forEach((marker) => marker.setMap(null));
      this.markers = Object.values(this.spots).reduce((accumulator, spot) => {
        accumulator[spot.id] = this.makeMarker(spot);
        this.bounds.extend(accumulator[spot.id].position);
        return accumulator;
      }, {});

      if (Object.keys(this.markers).length > 1) {
        this.map.fitBounds(this.bounds);
        this.map.panToBounds(this.bounds);
      } else if (Object.keys(this.markers).length) {
        this.map.setZoom(15);
        this.map.setCenter(this.markers[Object.keys(this.markers)[0]].position);
      }
    };

    this.reCreateEmbla = () => {
      const spotListElement = document.getElementById("target-spot-map-list");
      const html = Object.values(this.spots)
        .filter((spot) => !spot.selected)
        .map((spot, index) => this.makeSpotHtml(spot, index))
        .join("");
      spotListElement.innerHTML = `
        <section class="c-embla js-embla-spotmap">
          <section class="c-embla__container">
            <ul class="c-embla__list">${html}</ul>
          </section>
          <button type="button" class="c-embla-arrows__link c-embla-arrows__link--prev" style="position: absolute; inset: calc(50% - 16px) auto 0 8px"></button>
          <button type="button" class="c-embla-arrows__link c-embla-arrows__link--next" style="position: absolute; inset: calc(50% - 16px) 8px 0 auto"></button>
        </section>
      `;

      const emblaInstances = embla("js-embla-spotmap");
      this.emblaApi = emblaInstances[0]?.emblaApi || null;

      this.emblaApi?.on("select", () => {
        const index = this.emblaApi.selectedScrollSnap();
        const spotId = this.emblaApi.slideNodes()[index]?.querySelector(".c-spotmap__item")?.dataset.markerId;
        for (const [id, marker] of Object.entries(this.markers)) {
          if (id === spotId) {
            marker.setOptions({ icon: this.iconHash(marker.type, true, marker.selected), zIndex: 2 });
            this.map.panTo(marker.getPosition());
          } else {
            marker.setOptions({ icon: this.iconHash(marker.type, false, marker.selected), zIndex: 1 });
          }
        }
      });
      bindMicromodalEventTrigger(spotListElement);
    };

    this.getSpots = (type = null) => {
      Rails.ajax({
        url: this.url,
        type: "GET",
        dataType: "json",
        data: Object.entries({ type: type })
          .flatMap((e) => (e[1] ? e.join("=") : []))
          .join("&"),
        error: (response) => {
          console.error(response);
        },
        success: (response) => {
          this.images = response.images || this.images;
          this.spots = response.spots || {};
          this.makeMarkers();
          this.reCreateEmbla();
          if (this.currentSpotSlug) {
            const currentMarker = Object.values(this.markers).find((marker) => marker.slug === this.currentSpotSlug);
            if (currentMarker) this.maps.event.trigger(currentMarker, "click");
          }
        },
      });
    };

    this.getFilterdSpots = ({ target }) => {
      const element = target.closest(".js-map-spots-filter");
      if (element.classList.contains("c-spotmap-navigation__item--selected")) return;

      document.querySelector(".c-spotmap-navigation__item--selected").classList.remove("c-spotmap-navigation__item--selected");
      element.classList.add("c-spotmap-navigation__item--selected");
      // eslint-disable-next-line no-undef
      onclickcatalyst_clickcount(`map-filer-${element.dataset.type}`, "event16");
      this.getSpots(element.dataset.type);
    };

    this.toggleSpotMap = () => {
      if (!Object.keys(this.images).length || !Object.keys(this.spots).length) {
        this.getSpots();
      } else {
        if (this.currentSpotSlug) {
          const currentMarker = Object.values(this.markers).find((marker) => marker.slug === this.currentSpotSlug);
          if (currentMarker) this.maps.event.trigger(currentMarker, "click");
        }
      }

      // NOTE: SPはmicromodalで表示される。PCはここで実装
      if (document.querySelector(".js-slide-panel")) this.toggleSlide();
    };

    this.toggleFleshSpotMap = ({ target }) => {
      url = target.dataset.url;
      this.getSpots();

      // NOTE: SPはmicromodalで表示される。PCはここで実装
      if (document.querySelector(".js-slide-panel")) this.toggleSlide();
    };

    this.toggleSpotMapCompare = () => {
      if (!Object.keys(this.images).length || !Object.keys(this.spots).length) this.getSpots();

      // NOTE: SPはmicromodalで表示される。PCはここで実装
      if (document.querySelector(".js-slide-panel") && !document.querySelector(".c-slide-panel--open")) this.toggleSlide();
    };

    this.currentGeolocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const marker = new this.maps.Marker({
              id: "current",
              position: new this.maps.LatLng(position.coords.latitude, position.coords.longitude),
              map: this.map,
              icon: { url: this.images["here"], scaledSize: new this.maps.Size(40, 40), zIndex: 1 },
            });
            this.bounds.extend(marker.position);
            this.map.fitBounds(this.bounds);
            this.map.panToBounds(this.bounds);
          },
          () => alert("位置情報を取得できませんでした"),
        );
      } else {
        alert("ブラウザは位置情報をサポートしていません");
      }
    };
  }
}

function bindSpotMapEventTriggers() {
  if (!document.querySelector(".js-map-canvas-button[data-url]")) return;

  googlemap.then((maps) => {
    const spotMap = new SpotMap(maps, document.querySelector(".js-map-canvas-button[data-url]").dataset.url);
    [].forEach.call(document.querySelectorAll(".js-map-canvas-button"), (element) => {
      if (element.dataset.slug) {
        element.addEventListener("click", (event) => {
          spotMap.toggleSpotMapCompare();
          spotMap.currentSpotSlug = Object.keys(spotMap.slugs).find((key) => spotMap.slugs[key].includes(event.target.dataset.slug));
          if (Object.keys(spotMap.markers).length) {
            const marker = Object.values(spotMap.markers).find((marker) => marker.slug === spotMap.currentSpotSlug);
            if (marker) spotMap.maps.event.trigger(marker, "click");
          }
        });
      } else if (element.dataset.reflesh) {
        element.addEventListener("click", spotMap.toggleFleshSpotMap);
      } else {
        element.addEventListener("click", spotMap.toggleSpotMap);
      }
    });
    [].forEach.call(document.querySelectorAll(".js-map-spots-position-button"), (element) => {
      element.addEventListener("click", spotMap.currentGeolocation);
    });
    [].forEach.call(document.querySelectorAll(".js-map-spots-filter"), (element) => {
      element.addEventListener("click", spotMap.getFilterdSpots);
    });
  });
}

module.exports = { bindSpotMapEventTriggers };

document.addEventListener("DOMContentLoaded", () => {
  bindSpotMapEventTriggers();
});
